'use client'

import React from 'react'
import Link from 'next/link'
import styled from 'styled-components'
import { useAuth } from '@/contexts/AuthContext'
import { useLanguage } from '@/contexts/LanguageContext'

/*
 * Landing pública de config-strike (home antes del login).
 * Paleta tomada de la webapp real: bg #050208, indigo #6366f1 (dominante),
 * cian #3efffc y magenta #ff00ff como neón, fuentes Squada One / Chakra Petch / Roboto.
 * Todo offline-first: mocks en CSS/canvas, sin assets externos.
 */

const SteamIcon = (props) => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" {...props}>
        <path d="M12 2a10 10 0 0 0-9.96 9.2l5.36 2.2a2.83 2.83 0 0 1 1.6-.5l2.39-3.46v-.05a3.78 3.78 0 1 1 3.78 3.78h-.09l-3.4 2.43a2.84 2.84 0 0 1-5.65.4L2.2 14.9A10 10 0 1 0 12 2Zm-3.2 15.18.98.4a2.13 2.13 0 1 0 1.18-2.78l-1.02-.42a1.64 1.64 0 0 1 .93 3.05Zm7-6.15a2.52 2.52 0 1 0 0-5.04 2.52 2.52 0 0 0 0 5.04Z" />
    </svg>
)

export default function Landing() {
    const { isAuthenticated } = useAuth()
    const { t } = useLanguage()
    const editorHref = '/editor/crosshair'

    return (
        <Root>
            {/* Sin header ni logo propios: la home es una sección de la app y el
                shell (MainSideBar) ya pone la marca y la navegación. */}

            {/* ================= HERO ================= */}
            <section className="hero" id="intro">
                <div className="wrap">
                    <div className="hero-text">
                        <span className="eyebrow">exec autoexec.cfg</span>
                        <Link href={editorHref} className="hero-title-link" aria-label={t('LANDING_CTA_EDITOR')}>
                            <h1 className="glitch" data-text={`${t('LANDING_HERO_TITLE_1')} ${t('LANDING_HERO_TITLE_2')}`}>{t('LANDING_HERO_TITLE_1')}<br /><span className="accent">{t('LANDING_HERO_TITLE_2')}</span></h1>
                        </Link>
                        <p className="lead">{t('LANDING_HERO_LEAD')}</p>

                        <div className="hero-cta">
                            {isAuthenticated
                                ? <Link href={editorHref} className="btn btn-steam btn-lg">{t('LANDING_CTA_EDITOR')}</Link>
                                : <Link href="/login" className="btn btn-steam btn-lg"><SteamIcon className="steam-ico" />{t('LANDING_CTA_STEAM_LONG')}</Link>}
                            <a href="#patreon" className="btn btn-ghost btn-lg">{t('LANDING_CTA_DOWNLOAD')}</a>
                        </div>

                        <div className="hero-micro">
                            <span>{t('LANDING_HERO_MICRO_1')}</span>
                            <span>{t('LANDING_HERO_MICRO_2')}</span>
                            <span>{t('LANDING_HERO_MICRO_3')}</span>
                        </div>
                    </div>

                </div>
            </section>

            {/* ================= PATREON ================= */}
            <section className="patreon" id="patreon">
                <div className="wrap">
                    <div className="patreon-card">
                        <div className="patreon-grid">
                            <div className="patreon-left">
                                <div className="pat-head">
                                    <span className="pat-mark"><span /></span>
                                    <div style={{ flexDirection: 'column' }}>
                                        <span className="eyebrow patreon-eb">{t('LANDING_PATREON_EYEBROW')}</span>
                                        <h2>{t('LANDING_PATREON_TITLE')}</h2>
                                    </div>
                                </div>
                                <p>{t('LANDING_PATREON_TEXT')}</p>
                                <button className="btn btn-patreon btn-lg">{t('LANDING_PATREON_CTA')}</button>
                            </div>

                            <div className="patreon-right">
                                <div className="pat-col">
                                    <h4>{t('LANDING_PATREON_UNLOCK_TITLE')}</h4>
                                    <ul>
                                        <li>{t('LANDING_PATREON_LI_1')}</li>
                                        <li>{t('LANDING_PATREON_LI_2')}</li>
                                        <li>{t('LANDING_PATREON_LI_3')}</li>
                                        <li>{t('LANDING_PATREON_LI_4')}</li>
                                        <li>{t('LANDING_PATREON_LI_5')}</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ================= ABOUT ================= */}
            <section className="about" id="about">
                <div className="wrap about-grid">
                    <div className="about-card">
                        <div><span className="c">{t('LANDING_ABOUT_CODE_WHOAMI')}</span></div>
                        <div><span className="k">{t('LANDING_ABOUT_CODE_K_DEV')}</span> = &quot;{t('LANDING_ABOUT_CODE_V_DEV')}&quot;</div>
                        <div><span className="k">{t('LANDING_ABOUT_CODE_K_REASON')}</span> = &quot;{t('LANDING_ABOUT_CODE_V_REASON')}&quot;</div>
                        <div><span className="s">{t('LANDING_ABOUT_CODE_K_PROJECT')}</span> = &quot;{t('LANDING_ABOUT_CODE_V_PROJECT')}&quot;</div>
                        <div><span className="s">{t('LANDING_ABOUT_CODE_K_GROWS')}</span> = &quot;{t('LANDING_ABOUT_CODE_V_GROWS')}&quot;</div>
                        <div><span className="c">{t('LANDING_ABOUT_CODE_LOGIN')}</span></div>
                    </div>
                    <div style={{ flexDirection: 'column' }}>
                        <span className="eyebrow">{t('LANDING_ABOUT_EYEBROW')}</span>
                        <h2>{t('LANDING_ABOUT_TITLE')}</h2>
                        <p>{t('LANDING_ABOUT_P1')}</p>
                        <p>{t('LANDING_ABOUT_P2_1')}<b>{t('LANDING_ABOUT_P2_BOLD')}</b>{t('LANDING_ABOUT_P2_2')}</p>
                        <p className="muted">{t('LANDING_ABOUT_P3')}</p>
                        <p className="sign">{t('LANDING_ABOUT_SIGN')}</p>
                    </div>
                </div>
            </section>

            {/* Sin footer de sitio (marca + nav + CTA duplicados del sidebar).
                Queda solo la línea legal: el descargo de marca de Valve conviene
                que siga visible en algún lado. */}
            <div className="legal">
                <div className="wrap">
                    <span>{t('LANDING_FOOT_LEGAL_1')}</span>
                    <span>{t('LANDING_FOOT_LEGAL_2')}</span>
                </div>
            </div>
        </Root>
    )
}

const Root = styled.div`
  --ink:#050208; --panel:#0d1117; --panel-2:#12151d; --panel-3:#1a1f2b;
  --line:rgba(255,255,255,.09); --line-2:rgba(255,255,255,.16);
  --fg:#eef1f5; --muted:#98a2b0; --muted-2:#6f7887;
  --accent:#6366f1; --accent-2:#818cf8; --accent-soft:rgba(99,102,241,.16);
  --cyan:#3efffc; --magenta:#ff00ff;
  --steam:#66c0f4;
  --celeste:#74acdf; --sol:#f6b40e; --ok:#4ade80;
  --font-display:var(--font-squada),'Squada One',cursive;
  --font-tech:var(--font-chakra),'Chakra Petch',monospace;
  --font-body:var(--font-roboto),'Roboto',system-ui,sans-serif;
  --wrap:1180px;

  /* La home es una sección de la app: vive dentro de la columna de contenido
     de DashboardPage, que es la que scrollea. NADA de position:fixed acá — eso
     era para cuando la landing ocupaba la pantalla entera por su cuenta.
     El fondo va transparente a propósito: así se ve el AnimatedBackground de
     la app y la sección no parece un iframe pegado. Los degradés propios se
     mantienen porque son parte del diseño de la home. */
  display:block; width:100%; overflow-x:hidden; scroll-behavior:smooth;
  color:var(--fg); font-family:var(--font-body); line-height:1.6;
  background-image:
    radial-gradient(1200px 600px at 82% -10%, rgba(99,102,241,.16), transparent 60%),
    radial-gradient(900px 500px at 0% 8%, rgba(62,255,252,.07), transparent 55%);

  /* neutralizar el global div{display:flex;row} de globalStyles: volver a los
     valores iniciales (block/row) para que cada contenedor flex fije lo suyo. */
  div{ display:block; flex-direction:initial; justify-content:initial; align-items:initial; }

  *{ box-sizing:border-box; }
  a{ color:inherit; text-decoration:none; }
  canvas{ display:block; }
  section{ position:relative; }
  section[id]{ scroll-margin-top:84px; }
  .wrap{ max-width:var(--wrap); margin:0 auto; padding:0 22px; width:100%; }

  .eyebrow{ font-family:var(--font-tech); font-size:.74rem; letter-spacing:.14em;
    text-transform:uppercase; color:var(--cyan); display:inline-flex; gap:.5em; align-items:center; }
  .eyebrow::before{ content:"//"; color:var(--muted-2); }
  .eyebrow.celeste{ color:var(--celeste); }
  .eyebrow.patreon-eb{ color:#f96854; }

  h1,h2,h3,h4{ font-family:var(--font-display); text-transform:uppercase; font-weight:400;
    line-height:1; letter-spacing:.01em; text-wrap:balance; margin:0; }

  .btn{ font-family:var(--font-display); text-transform:uppercase; font-weight:400;
    letter-spacing:.03em; font-size:1rem; border:none; cursor:pointer; white-space:nowrap;
    padding:12px 20px; border-radius:9px; display:inline-flex; align-items:center; justify-content:center; gap:10px;
    transition:transform .12s, box-shadow .18s, background .18s, border-color .18s; }
  .btn:active{ transform:translateY(1px); }
  .btn-vip{ background:var(--accent); color:#fff; box-shadow:0 6px 20px -6px rgba(99,102,241,.7); }
  .btn-vip:hover{ background:var(--accent-2); box-shadow:0 8px 26px -6px rgba(129,140,248,.85); }
  .btn-ghost{ background:transparent; color:var(--fg); border:1px solid var(--line-2); }
  .btn-ghost:hover{ border-color:var(--cyan); color:var(--cyan); }
  .btn-steam{ background:linear-gradient(180deg,#2a4a63,#1b3346); color:#eaf6ff;
    border:1px solid rgba(102,192,244,.4); box-shadow:0 8px 26px -10px rgba(102,192,244,.6); }
  .btn-steam:hover{ border-color:var(--steam); }
  .btn-lg{ padding:16px 26px; font-size:1.18rem; border-radius:11px; }

  /* (El HEADER de sitio se fue: la home es una sección de la app y la
     marca + navegación las pone el MainSideBar.) */

  /* HERO */
  .hero{ padding:64px 0 78px; }
  .hero-text{ display:flex; flex-direction:column; align-items:flex-start; }
  .hero-title-link{ display:inline-block; cursor:pointer; color:inherit; text-decoration:none; }
  .hero h1{ font-size:clamp(2.2rem,4.3vw,3.5rem); margin:14px 0 0; }
  .hero h1 .accent{ color:var(--accent-2); }
  /* Glitch por text-shadow sobre el texto REAL (no copias posicionadas), así
     queda siempre alineado sin importar tamaño/fuente/salto de línea. */
  .glitch{ position:relative; }
  .glitch:hover{ animation:cs-glitch .38s steps(2,end) infinite; }
  @keyframes cs-glitch{
    0%{ text-shadow:2px 0 0 var(--magenta), -2px 0 0 var(--cyan); }
    25%{ text-shadow:-2px -1px 0 var(--magenta), 2px 1px 0 var(--cyan); }
    50%{ text-shadow:2px 1px 0 var(--magenta), -2px -1px 0 var(--cyan); }
    75%{ text-shadow:-2px 0 0 var(--magenta), 2px 0 0 var(--cyan); }
    100%{ text-shadow:2px 0 0 var(--magenta), -2px 0 0 var(--cyan); }
  }
  @media (prefers-reduced-motion:reduce){
    .glitch:hover{ animation:none; text-shadow:2px 0 0 var(--magenta), -2px 0 0 var(--cyan); }
  }
  .hero .lead{ color:var(--muted); font-size:1.12rem; max-width:46ch; margin:20px 0 4px; }
  .hero-cta{ display:flex; gap:14px; flex-wrap:wrap; margin-top:30px; }
  .hero-micro{ margin-top:20px; display:flex; gap:18px; flex-wrap:wrap;
    font-family:var(--font-tech); font-size:.78rem; color:var(--muted-2); }
  .hero-micro span{ display:inline-flex; gap:7px; align-items:center; }
  .hero-micro span::before{ content:""; width:6px; height:6px; border-radius:50%;
    background:var(--ok); box-shadow:0 0 8px var(--ok); }

  /* PATREON */
  .patreon{ padding:82px 0; }
  .patreon-card{ border:1px solid var(--line); border-radius:20px; overflow:hidden; display:flex; flex-direction:column;
    background:linear-gradient(180deg,var(--panel-2),var(--panel)); }
  .patreon-grid{ display:grid; grid-template-columns:1.05fr .95fr; gap:52px; align-items:start; padding:44px; }
  .patreon-left{ display:flex; flex-direction:column; align-self:stretch; }
  .patreon-left .btn-patreon{ margin-top:auto; align-self:flex-start; }
  .pat-head{ display:flex; flex-direction:row; gap:18px; align-items:center; margin-bottom:16px; }
  .patreon-right{ display:flex; flex-direction:column; align-items:flex-start; gap:22px; }
  .pat-mark{ width:52px; height:52px; border-radius:14px; flex:none; display:flex; align-items:center; justify-content:center;
    background:var(--ink); border:1px solid var(--line-2); }
  .pat-mark span{ width:8px; height:26px; background:#f96854; border-radius:2px; position:relative; display:block; }
  .pat-mark span::after{ content:""; position:absolute; left:13px; top:-1px; width:18px; height:18px;
    border-radius:50%; background:#f96854; }
  .patreon-left h2{ font-size:clamp(2rem,4vw,2.8rem); }
  .patreon-left p{ color:var(--muted); max-width:52ch; margin:0 0 30px; }
  .pat-cols{ display:grid; grid-template-columns:1fr 1fr; gap:0; }
  .pat-col{ padding:0; display:flex; flex-direction:column; width:100%; }
  .pat-col + .pat-col{ border-left:1px solid var(--line); }
  .pat-col h4{ font-family:var(--font-tech); text-transform:uppercase; font-size:.72rem; font-weight:600;
    letter-spacing:.09em; color:var(--accent-2); margin:0 0 16px; display:flex; flex-direction:row; gap:8px; align-items:center; }
  .pat-col.next h4{ color:var(--cyan); }
  .pat-col ul{ list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:12px; }
  .pat-col li{ display:flex; flex-direction:row; gap:11px; font-size:.98rem; }
  .pat-col li::before{ content:""; width:8px; height:14px; flex:none; margin:2px 6px 0 2px;
    border:solid var(--accent-2); border-width:0 2px 2px 0; transform:rotate(45deg); }
  .pat-col.next li{ color:var(--muted); }
  .pat-col.next li::before{ border-color:var(--cyan); }
  .pat-foot{ padding:22px 40px 34px; border-top:1px solid var(--line); display:flex; flex-direction:row;
    gap:16px; align-items:center; flex-wrap:wrap; }
  .btn-patreon{ background:#f96854; color:#2a0f0b; }
  .btn-patreon:hover{ background:#ff7a67; }
  .pat-foot small, .pat-note{ color:var(--muted-2); font-family:var(--font-tech); font-size:.76rem; }

  /* ABOUT */
  .about{ padding:78px 0 40px; }
  .about-grid{ display:grid; grid-template-columns:.9fr 1.3fr; gap:50px; align-items:start; }
  .about-card{ border:1px solid var(--line); border-radius:16px; background:var(--panel); padding:26px;
    font-family:var(--font-tech); font-size:.82rem; color:var(--muted); line-height:1.9;
    display:flex; flex-direction:column; gap:2px; }
  .about-card .k{ color:var(--accent-2); }
  .about-card .s{ color:var(--cyan); }
  .about-card .c{ color:var(--muted-2); }
  .about h2{ font-size:clamp(2.1rem,4vw,3rem); margin-bottom:18px; }
  .about p{ color:var(--fg); opacity:.92; margin:0 0 16px; max-width:62ch; }
  .about p.muted{ color:var(--muted); }
  .about .sign{ font-family:var(--font-display); text-transform:uppercase; letter-spacing:.04em;
    color:var(--accent-2); margin-top:8px; font-size:1.1rem; }
  /* LEGAL (lo único que sobrevive del footer de sitio) */
  .legal{ border-top:1px solid var(--line); margin-top:40px; padding:26px 0 34px; }
  .legal .wrap{ display:flex; flex-direction:row; justify-content:space-between; gap:16px; flex-wrap:wrap;
    font-family:var(--font-tech); font-size:.72rem; color:var(--muted-2); }

  .steam-ico{ flex:none; }

  @media (max-width:1000px){
    .pat-cols{ grid-template-columns:1fr; }
    .pat-col + .pat-col{ border-left:none; border-top:1px solid var(--line); }
    .about-grid{ grid-template-columns:1fr; gap:30px; }
    .patreon-grid{ grid-template-columns:1fr; gap:30px; padding:32px; }
  }
`
